import { Component, OnInit, ViewChild } from '@angular/core';
import { ColmecWorkingOrderStepComponent } from '../colmec-working-order-step/colmec-working-order-step.component';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import {
  OperatorFunction,
  Observable,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  forkJoin,
  of,
  map,
  first,
  filter,
  tap,
  merge,
  Subject
} from 'rxjs';
import {
  AuthService,
  PriceService,
  Product,
  ProductService,
  gungAddRemoveSpinner,
  Customer,
  SelectedCustomerService,
  CustomerProductPrice
} from 'gung-standard';
import { SearchRequest } from 'gung-list';
import { GungUser as User } from 'gung-standard';
import {
  ColmecWorkOrderServiceModel,
  ColmecTirePrice
} from '../../colmec-working-order-wrapper/colmec-working-order-wrapper.component';
import { ColmecServiceWorkingOrderService } from 'projects/colmec/src/app/services/colmec-service-working-order.service';
import { ColmecPartnerAgreementsService } from 'projects/colmec/src/app/services/colmec-partner-agreements.service';

@Component({
    selector: 'colmec-working-order-services-step',
    templateUrl: './colmec-working-order-services-step.component.html',
    styleUrls: ['./colmec-working-order-services-step.component.scss'],
    standalone: false
})
export class ColmecWorkingOrderServicesStepComponent extends ColmecWorkingOrderStepComponent implements OnInit {
  @ViewChild('gungTextInputTypeahead', { static: true }) gungTextInputTypeahead: any;

  public form: FormGroup;
  public articles;
  protected currentUser: User;
  protected selectedCustomer: Customer;
  public calculatedTotals: { qty: number; total: number; currency: string };
  protected servicePriceListId: string;

  cachedServicesWorkingOrder: any[];

  resultFormatterItemId = (item: any) => {
    if (!!item.name && !!item.id) {
      return `${item.name} - ${item.id}`;
    } else if (!!item.name) {
      return `${item.name}`;
    } else {
      return '';
    }
  };

  itemIdSearchTypeahead: OperatorFunction<string, readonly Product[]> = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());
    const clicksWithClosedPopup$: Subject<string> = this.gungTextInputTypeahead.click$.pipe(
      filter(() => !this.gungTextInputTypeahead.instance.isPopupOpen())
    );
    const inputFocus$: Subject<string> = this.gungTextInputTypeahead.focus$;

    return merge(debouncedText$, inputFocus$, clicksWithClosedPopup$).pipe(
      switchMap(term => {
        try {
          return of(
            this.cachedServicesWorkingOrder.filter(service => {
              const terms: string[] = term.split(' ');
              let testTerms: string[] = [
                service?.id?.toLowerCase() || '',
                ...service?.name?.split(' ')?.map(e => e?.toLowerCase())
              ];

              for (const term of terms) {
                for (const test of testTerms) {
                  if (test?.includes(term.toLowerCase())) {
                    return true;
                  }
                }
              }

              return false;
            })
          );
        } catch (error) {
          return of([]);
        }
      }),
      map(services => {
        return services.length < 1 ? [] : services;
      })
    );
  };

  public noServicesSelected: boolean = false;

  constructor(
    protected authService: AuthService,
    protected selectedCustomerService: SelectedCustomerService,
    protected productService: ProductService,
    protected priceService: PriceService,
    protected colmecPartnerAgreementsService: ColmecPartnerAgreementsService,
    protected colmecServiceWorkingOrderService: ColmecServiceWorkingOrderService
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.onNextBtnClicked.subscribe(_ => this.handleNextButtonClicked());
    this.onPreviousBtnClicked.subscribe(_ => this.handlePreviousButtonClicked());
    this.initForm();
    this.authService
      .getCurrentUser()
      .pipe(
        first(),
        switchMap(currentUser =>
          forkJoin({
            currentUser: of(currentUser),
            selectedCustomer: this.selectedCustomerService.getSelectedCustomer().pipe(first())
          })
        )
      )
      .subscribe(({ currentUser, selectedCustomer }) => {
        this.currentUser = currentUser;
        this.selectedCustomer = selectedCustomer;
        this.calculateTotals();
      });

    this.colmecPartnerAgreementsService
      .getPartnerAgreementById(this.workingOrder.agreementId)
      .pipe(
        first(),
        tap(agreement => (this.servicePriceListId = agreement.servicePriceListId)),
        switchMap(agreement =>
          this.colmecServiceWorkingOrderService
            .getWorkingOrderServicesByPriceList(agreement.servicePriceListId)
            .pipe(first())
        )
      )
      .subscribe(services => (this.cachedServicesWorkingOrder = services.filter(s => !s.id.includes('-k'))));
  }

  public getLabel(): string {
    return 'SERVICES';
  }

  private handleNextButtonClicked(): void {
    /*if (this.workingOrder.services.length === 0) {
      this.noServicesSelected = true;
      return;
    }*/

    this.stepDone.emit(this.workingOrder);
  }

  private handlePreviousButtonClicked(): void {
    this.stepPrevious.emit(this.workingOrder);
  }

  protected initForm(): void {
    this.form = new FormGroup({
      article: new FormControl('', [Validators.required]),
      qty: new FormControl('', [Validators.required])
    });
  }

  public selectArticle({ item, preventDefault, target }) {
    this.form.get('article').setValue(item);
  }

  protected addToWorkingOrder({ target }): void {
    gungAddRemoveSpinner(target);
    const rawForm = this.form.getRawValue();

    this.colmecServiceWorkingOrderService
      .getWorkingOrderServicePrice(this.servicePriceListId, rawForm.article.id)
      .pipe(first())
      .subscribe(servicePrice => {
        const tirePrice: ColmecTirePrice = {
          id: null,
          name: null,
          extra: {},
          value: servicePrice.customerNetPrice.value,
          currency: servicePrice.customerNetPrice.currencyCode,
          discountPercentage: servicePrice.customerDiscountPercent
        };

        if (!!tirePrice.discountPercentage) {
          tirePrice.valueNoDiscount = servicePrice.customerGrossPrice.value;
        }

        const service: ColmecWorkOrderServiceModel = {
          id: rawForm.article.id,
          qty: rawForm.qty,
          name: rawForm.article.name,
          extra: {},
          price: tirePrice
        };

        this.workingOrder.services.push(service);
        this.noServicesSelected = false;
        this.calculateTotals();
        gungAddRemoveSpinner(target);
        this.resetForm(this.form);
      });
  }

  protected removeFromWorkingOrder(index: number): void {
    this.workingOrder.services.splice(index, 1);
    this.calculateTotals();
  }

  protected calculateTotals(): void {
    if (this.workingOrder.services.length === 0) {
      this.calculatedTotals = undefined;
      return;
    }

    const totals: { qty: number; total: number; currency: string } = { qty: 0, total: 0, currency: '' };
    for (const service of this.workingOrder.services) {
      totals.qty += service.qty;
      totals.total += service.price.value * service.qty;
      totals.currency = service.price.currency;
    }
    this.calculatedTotals = totals;
  }
}
